//#region Util Imports

import * as _ from 'underscore'

//#endregion

import { ColumnModel } from '../../../../../../../api/api.models'

import { OpOperator, OpActivity } from '../../../../../../../../models/core'
import { Enums, Activity } from '../../../../../../../../models/common'
import { ColumnInfo } from '../../../../../../../../models/custom'
import { IReferenceType } from '../../../../../../../../models/util/interfaces'

import { Dictionary } from '../../../../../../../../helpers/dictionary'
import { DataProviderListResult } from '../../../../../../../../data-provider'
import { getEmptyObjectListResult } from '../../../../../../../../data-provider/utils'

import { GridService } from '../../../../../../../../services/grid'
import { OperatorSecurityService } from '../../../../../../../../services/operator'
import { ReferenceTypeService } from '../../../../../../../../services/reference-type'
import { UserSessionService } from '../../../../../../../../services/user-session'

import { IMRBaseControlController } from '../../../../../../../common/base'
import { GridTypes } from '../../../../../../../common/grid'
import { IMREditGridOptions, IIMREditGrid, IMREditGridInitializedArgs, IMREditGridBeforeEditArgs, IMREditGridSubmitArgs, IMREditGridGetObjectIdArgs, IMREditGridSetObjectIdArgs } from '../../../../../../../common/edit-grid'
import { IMRSelectGridPopupOptions, IIMRSelectGridPopup, IMRSelectGridPopupInitializedArgs } from '../../../../../../../common/select-grid/popup'
import { IMRSelectGridObjectSelected } from '../../../../../../../common/select-grid'

import { IOperatorDetailsSecurityCustom, OperatorDetailsSecurityCustomOptions } from './operator-details.security.custom.interfaces'

export class OperatorDetailsSecurityRolesController
    extends
    IMRBaseControlController<IOperatorDetailsSecurityCustom, OperatorDetailsSecurityCustomOptions>
    implements
    IOperatorDetailsSecurityCustom {

    //#region Consts

    private get OPERATOR_CUSTOM_ACTIVITIES_GRID_NAME() { return 'customActivities' }
    private get OPERATOR_SELECT_CUSTOM_ACTIVITIES_GRID_NAME() { return 'usersSecurityActivitiesGrid' }
    private get OPERATOR_CUSTOM_ACTIVITIES_EDIT_REFERENCE_GRID_NAME() { return 'UsersDetailsReferenceTypeGrid' }

    //#endregion


    //#region Members

    public isLoading: boolean
    private operatorToEdit: OpOperator
    private activityReferenceDict: Dictionary<IReferenceType[]>

    //#endregion


    //#region Ctor

    public constructor(
        private gridService: GridService,
        private operatorSecurityService: OperatorSecurityService,
        private referenceTypeService: ReferenceTypeService,
        private userSessionService: UserSessionService
    ) {
        super()
    }

    //#endregion


    //#region Component

    protected initialize(options: OperatorDetailsSecurityCustomOptions): JQueryPromise<void> {
        this.isLoading = true
        this.activityReferenceDict = options.activityReferenceDict
        this.setOperator(options.operator)

        return this.operatorSecurityService
            .loadActivityNavProperties(this.operatorToEdit)
            .then(() => {
                return $.when(this.initializeEditGrid(), this.initializeReferenceTypeSelectPopover())
            })
            .then(() => {
                this.isLoading = false
            })
    }

    //#endregion


    //#region IOperatorDetailsSecurityRoles Implementation

    public hasChanges(): boolean {
        return this.customPermissionsEditGrid != null ? this.customPermissionsEditGrid.hasChanges() : false;
    }

    public setOperator(operator: OpOperator): void {
        this.operatorToEdit = operator.clone(true, true, true, true)
    }

    public setActivityReferenceDict(dict: Dictionary<IReferenceType[]>): void {
        this.activityReferenceDict = dict
    }

    public reset(): JQueryPromise<void> {
        this.isLoading = true

        return this.operatorSecurityService
            .loadActivityNavProperties(this.operatorToEdit)
            .always(() => {
                if (this.customPermissionsEditGrid != null) {
                    this.customPermissionsEditGrid.reset()
                }

                this.isLoading = false
            })
    }

    //#endregion


    //#region Grid Edit

    public customPermissionsEditGridOptions: IMREditGridOptions<OpActivity, OpActivity>
    private customPermissionsEditGrid: IIMREditGrid<OpActivity, OpActivity>
    private selectedGridActivity: OpActivity

    private initializeEditGrid(): void {
        const canEditActivities: boolean = this.userSessionService.getLoggedOperator().hasPermission(Activity.ALLOWED_OPERATOR_TO_EDIT_ACTIVITIES)

        $
            .when(
                this.operatorSecurityService.getCustomActivityColumns(
                    this.options.modules,
                    (activity: OpActivity) => this.deleteReferenceTypeFromActivity(activity),
                    (activity: OpActivity, buttonEl: JQuery) => this.openReferenceTypeSelectGrid(activity, buttonEl),
                    canEditActivities
                ),
                this.operatorSecurityService.getSelectCustomActivityColumns()
            )
            .then((customActivityColumns: ColumnModel[], selectCustomActivityColumns: ColumnModel[]) => {
                this.customPermissionsEditGridOptions = {
                    allowEditing: canEditActivities,

                    sourceGridOptions: {
                        addCustomFilters: true,
                        columnsDictionary: selectCustomActivityColumns,
                        idReferenceType: Enums.ReferenceType.IdActivity,
                        name: this.OPERATOR_SELECT_CUSTOM_ACTIVITIES_GRID_NAME,
                        type: GridTypes.PAGINATED_SMALL,

                        buttonsOptions: {
                            enabled: true,
                            isRefreshButonHidden: true
                        },

                        getDataSource: () => {
                            return this.referenceTypeService.getDictionaryReferenceObjectList<OpActivity>(Enums.ReferenceType.IdActivity)
                        },
                    },

                    targetGridOptions: {
                        addCustomFilters: true,
                        columnsDictionary: customActivityColumns,
                        idReferenceType: Enums.ReferenceType.IdActivity,
                        name: this.OPERATOR_CUSTOM_ACTIVITIES_GRID_NAME,
                        type: GridTypes.PAGINATED_SMALL,

                        allowColumnReordering: !this.userSessionService
                            .getLoggedOperator()
                            .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.OperatorSecurityCustomPermission),

                        editing: {
                            mode: "cell",
                            allowUpdating: true
                        },

                        buttonsOptions: {
                            enabled: true,
                            isRefreshButonHidden: true
                        },

                        dataSourceParser: (activity: OpActivity) => {
                            return {
                                additionalFields: [{
                                    key: 'moduleName',
                                    value: this.operatorSecurityService.getActivityModuleString(this.options.modules, activity)
                                }, {
                                    key: 'referenceName',
                                    value: this.operatorSecurityService.getActivityReferenceTypeString(this.activityReferenceDict, activity)
                                }]
                            }
                        },

                        getDataSource: () => {
                            return this.operatorSecurityService.filterCustomActivity(this.operatorToEdit.allActivities)
                        },
                    },

                    getObjectId: (e: IMREditGridGetObjectIdArgs<OpActivity, OpActivity>) => {
                        return e.object.idOperatorActivity
                    },

                    setObjectId: (e: IMREditGridSetObjectIdArgs<OpActivity, OpActivity>) => {
                        e.object.idOperatorActivity = e.id
                    },

                    onInitialized: (e: IMREditGridInitializedArgs<OpActivity, OpActivity>) => {
                        this.customPermissionsEditGrid = e.component
                    },

                    onDisposed: () => {
                        this.customPermissionsEditGrid = null
                    },

                    onBeforeEdit: (e: IMREditGridBeforeEditArgs<OpActivity, OpActivity>) => {
                        return this.prepareEditedActivity(e.object, e.editedData)
                    },

                    onSubmit: (e: IMREditGridSubmitArgs<OpActivity, OpActivity>) => {
                        super.callEvent(this.options.onSubmit, {
                            addedActivities: e.addedObjects,
                            modifiedActivities: e.modifiedObjects,
                            deletedActivities: e.deletedObjects
                        })
                    }
                }
            })
    }

    private prepareEditedActivity(activity: OpActivity, editedData: any): OpActivity {
        let activityToEdit: OpActivity = activity

        if (activity.idOperatorActivity > 0) {
            const existingActivity: OpActivity = _.find(this.operatorToEdit.allActivities, (existingActivity: OpActivity) => {
                return activity.idOperatorActivity === existingActivity.idOperatorActivity
            })

            if (existingActivity != null) {
                activityToEdit = existingActivity
            }
        }

        if (activityToEdit != null) {
            if (editedData.allow !== undefined) {
                activityToEdit.allow = editedData.allow
            }

            if (editedData.idModule !== undefined) {
                activityToEdit.idModule = editedData.idModule
            }

            if (editedData.referenceValue !== undefined) {
                activityToEdit.referenceValue = editedData.referenceValue
            }
        }

        return activityToEdit
    }

    private setReferenceObjectOnActivity(activity: OpActivity, referenceType: IReferenceType, referenceTypeDict: Dictionary<IReferenceType[]>): void {
        this.updateReferenceDict(activity, referenceType, referenceTypeDict)

        this.customPermissionsEditGrid.updateItem(activity, {
            referenceValue: referenceType.getReferenceKey()
        })
    }

    private deleteReferenceTypeFromActivity(activity: OpActivity): void {
        this.customPermissionsEditGrid.updateItem(activity, {
            referenceValue: null
        })
    }

    private updateReferenceDict(activity: OpActivity, referenceType: IReferenceType, referenceTypeDict: Dictionary<IReferenceType[]>): void {
        if (referenceType != null) {
            if (!referenceTypeDict.containsKey(activity.idReferenceType)) {
                referenceTypeDict.add(activity.idReferenceType, [])
            }

            const existingReferenceTypes: IReferenceType[] = referenceTypeDict.tryGetValue(activity.idReferenceType)
            const isAlreadyAdded: boolean = _.find(existingReferenceTypes, (existingReferenceType: IReferenceType) => {
                return existingReferenceType.getReferenceKey() === referenceType.getReferenceKey()
            }) != null

            if (!isAlreadyAdded) {
                referenceTypeDict
                    .tryGetValue(activity.idReferenceType)
                    .push(referenceType)
            }
        }
    }

    //#endregion


    //#region Popover

    public editReferenceValueSelectGridPopoverOptions: IMRSelectGridPopupOptions<IReferenceType>
    private editReferenceValueSelectGridPopover: IIMRSelectGridPopup<IReferenceType>
    private selectedColumns: ColumnModel[]

    private initializeReferenceTypeSelectPopover() {
        if (this.editReferenceValueSelectGridPopover == null) {
            this.editReferenceValueSelectGridPopoverOptions = {
                selectGridOptions: {
                    gridOptions: {
                        name: this.OPERATOR_CUSTOM_ACTIVITIES_EDIT_REFERENCE_GRID_NAME,
                        type: GridTypes.PAGINATED_SMALL,
                        columnsDictionary: [],
                        buttonsOptions: { enabled: true },

                        getDataSourceMethod: (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string): JQueryPromise<DataProviderListResult<any>> => {
                            if (this.selectedGridActivity != null) {
                                return this.referenceTypeService.getReferenceObjects(this.selectedGridActivity.idReferenceType, {
                                    filterOptions: {
                                        columns,
                                        customWhereClause
                                    },
                                    pagingOptions: {
                                        pageNumber: pageNbr,
                                        pageSize,
                                    }
                                })
                            }

                            return $.Deferred().resolve(getEmptyObjectListResult()).promise()
                        }
                    },

                    onObjectSelected: (e: IMRSelectGridObjectSelected<IReferenceType>) => {
                        this.setReferenceObjectOnActivity(this.selectedGridActivity, e.selectedObject, this.activityReferenceDict)
                    }
                },

                popoverOptions: {
                    innerOptions: {}
                },

                onInitialized: (e: IMRSelectGridPopupInitializedArgs<IReferenceType>) => {
                    this.editReferenceValueSelectGridPopover = e.component
                },

                onDisposed: () => {
                    this.editReferenceValueSelectGridPopover = null
                },

                onOverlayContentInitialized: () => {
                    this.editReferenceValueSelectGridPopover.getSelectGrid().getGrid().setColumnsDictionary(this.selectedColumns)
                }
            }
        }
    }

    private openReferenceTypeSelectGrid(gridActivity: OpActivity, buttonEl: JQuery): void {
        if (!this.editReferenceValueSelectGridPopover.getPopover().isVisible()) {
            if (gridActivity.idReferenceType != null) {
                this.gridService
                    .getPopupColumnModelByReferenceType(gridActivity.idReferenceType)
                    .then((columns: ColumnModel[]) => {
                        this.selectedColumns = columns
                        this.selectedGridActivity = gridActivity

                        this.editReferenceValueSelectGridPopover.getPopover().show(buttonEl)

                        if (this.editReferenceValueSelectGridPopover.getSelectGrid() != null) {
                            this.editReferenceValueSelectGridPopover.getSelectGrid().getGrid().setColumnsDictionary(columns)
                            this.editReferenceValueSelectGridPopover.getSelectGrid().getGrid().refresh()
                        }
                    })
            }
        }
        else {
            this.editReferenceValueSelectGridPopover.getPopover().hide()
        }
    }

    //#endregion

}
